import { useEffect } from "react";
import { Mail, Clock, MessageSquare, MapPin, ShieldCheck, Globe } from "lucide-react";
import { motion } from "framer-motion";

export default function ContactUs() {
  // Ensures page starts from the top on load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#fdfcf7] min-h-screen">
      {/* 
          HERO SECTION 
      */}
      <section className="py-20 px-6 border-b-4 border-foreground bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tighter mb-4">
              Contact <span className="text-primary italic">Support.</span>
            </h1>
            <p className="max-w-2xl text-xl font-bold text-muted-foreground">
              Our technical team is available to assist with integration, billing, and platform scaling inquiries.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 
          PRIMARY CONTACT CARDS 
      */}
      <section className="py-16 px-6 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {/* Email Card */}
          <div className="bg-white border-4 border-foreground p-8 shadow-brutal flex items-start gap-6">
            <div className="w-14 h-14 bg-primary border-2 border-foreground flex items-center justify-center shrink-0 shadow-brutal-sm">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-black uppercase mb-2 tracking-tight">Direct Inquiry</h2>
              <p className="font-bold text-primary text-xl mb-2">hello@dmflio.com</p>
              <p className="text-sm font-bold text-muted-foreground">For all technical and partnership requests.</p>
            </div>
          </div>

          {/* Response Time Card */}
          <div className="bg-butter border-4 border-foreground p-8 shadow-brutal flex items-start gap-6">
            <div className="w-14 h-14 bg-white border-2 border-foreground flex items-center justify-center shrink-0 shadow-brutal-sm">
              <Clock className="w-8 h-8 text-foreground" />
            </div>
            <div>
              <h2 className="text-2xl font-black uppercase mb-2 tracking-tight">Response Time</h2>
              <p className="font-bold text-xl mb-2">Under 24 Hours</p>
              <p className="text-sm font-bold text-muted-foreground italic">Priority support available for Pro Plan subscribers.</p>
            </div>
          </div>
        </div>

        {/* 
            SUPPORT CATEGORIES - Grid Layout 
        */}
        <div className="mb-20">
          <h2 className="text-3xl font-black uppercase mb-10 tracking-tighter italic flex items-center gap-3">
            <MessageSquare className="w-8 h-8 text-primary" /> Support Categories
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <SupportCategory title="Technical Integration" desc="API documentation, webhook setup, and automation rule troubleshooting." />
            <SupportCategory title="Billing & Accounts" desc="Subscription management, invoice requests, and plan upgrades." />
            <SupportCategory title="Feature Requests" desc="Submit feedback and suggest new platform capabilities for our roadmap." />
          </div>
        </div>

        {/* 
            BUSINESS INFORMATION 
        */}
        <div className="border-t-4 border-foreground pt-16 flex flex-col md:flex-row justify-between items-start gap-12 pb-20">
          <div className="max-w-md">
            <h3 className="text-3xl font-black uppercase mb-6 tracking-tighter">Global Operations</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3 font-bold">
                <MapPin className="text-primary w-5 h-5" />
                <span>Hyderabad, India</span>
              </div>
              <div className="flex items-center gap-3 font-bold">
                <Globe className="text-primary w-5 h-5" />
                <span>Serving Global Creators & Businesses</span>
              </div>
              <div className="flex items-center gap-3 font-bold">
                <ShieldCheck className="text-primary w-5 h-5" />
                <span>Official Meta Business Partner Protocols</span>
              </div>
            </div>
          </div>

          <div className="bg-foreground text-white p-8 border-4 border-foreground shadow-brutal-sm w-full md:w-auto">
            <h4 className="font-black uppercase text-sm mb-2 tracking-widest text-primary">Corporate Entity</h4>
            <p className="font-bold text-xl uppercase tracking-tighter italic">DmFlio Technologies</p>
            <p className="text-gray-400 font-medium text-sm mt-4">Compliance ID: Official API Integration Partner</p>
          </div>
        </div>
      </section>
    </div>
  );
}

// Sub-component for clean mapping
function SupportCategory({ title, desc }) {
  return (
    <div className="bg-white border-2 border-foreground p-6 shadow-brutal-sm hover:-translate-y-1 hover:-translate-x-1 hover:shadow-brutal transition-all cursor-default">
      <h3 className="text-lg font-black uppercase mb-3 tracking-tight border-b-2 border-foreground pb-2">{title}</h3>
      <p className="text-sm font-bold text-muted-foreground leading-relaxed">{desc}</p>
    </div>
  );
}